import {
  resolveSegment,
  resolveFullInstruction,
  repairPlaceholderIndices,
  type IngredientForPlaceholder,
} from './placeholders';
import type { UnitSystem } from './unit-display';

// One ingredient placeholder: a qty/unit/name token, or a portion token
// ({{portion:INDEX|AMOUNT|UNIT}}) which the AI uses for spoon/pinch measures
// (e.g. "1½ TL"). Portions must be included or their amount renders as plain
// text next to a highlighted name.
const PLACEHOLDER =
  String.raw`(?:\{\{(?:qty|unit|name):\d+\}\}|\{\{portion:\d+\|[^|}]+\|[^|}]+\}\})`;
// A maximal run of adjacent placeholders, separated only by whitespace.
// Highlighting the whole run (rather than just a strict qty+unit+name triplet)
// means the amount is marked even when it has no unit ("{{qty}} {{name}}"), is a
// portion measure, sits apart from its name, or is only a bare name.
const RUN_RE = new RegExp(`${PLACEHOLDER}(?:\\s+${PLACEHOLDER})*`, 'g');

/** One run of resolved instruction text, flagged if it names an ingredient. */
export interface InstructionSegment {
  text: string;
  /** True for a run of ingredient placeholders — the bit to highlight. */
  ingredient: boolean;
}

/**
 * Resolve a step instruction into flagged text segments.
 *
 * This is the shared walk behind BOTH renderers: `resolveInstructionNodes`
 * turns these into React `<mark>` nodes for the phone, and the Cast sender
 * ships them down the wire for the Nest Hub to render (#972). Keeping one
 * implementation is the point — the Hub used to receive a flat string from
 * `resolveFullInstruction`, so ingredient mentions were marked on the phone
 * and invisible on the screen you actually cook from.
 *
 * Every run is resolved through the canonical `resolveSegment`, so highlighted
 * text matches the ingredient list exactly. Falls back to a single unflagged
 * segment when the instruction carries no ingredient placeholders.
 */
export function resolveInstructionSegments(
  text: string,
  ingredients: IngredientForPlaceholder[],
  selectedServings: number,
  baseServings: number,
  locale: 'en' | 'de',
  unitSystem: UnitSystem,
): InstructionSegment[] {
  // Apply the shared index-repair pre-pass.
  const repaired = repairPlaceholderIndices(text, ingredients);

  const segments: InstructionSegment[] = [];
  let lastIdx = 0;

  const resolve = (raw: string) =>
    resolveSegment(raw, ingredients, selectedServings, baseServings, locale, unitSystem);

  // Resolve a plain-text segment via resolveSegment so segment-leading
  // whitespace (the space after a prior run's `}}`) is preserved instead of
  // stripped by line-level trimming.
  const pushText = (raw: string) => {
    if (!raw) return;
    const str = resolve(raw);
    if (str) segments.push({ text: str, ingredient: false });
  };

  // Ensure a space before a highlight when the preceding text doesn't supply
  // one — otherwise the mark butts against the previous word.
  const ensureSpaceBeforeMark = () => {
    const prev = segments[segments.length - 1];
    if (prev === undefined) return;
    if (!/\s$/.test(prev.text)) segments.push({ text: ' ', ingredient: false });
  };

  for (const m of repaired.matchAll(RUN_RE)) {
    if (m.index! > lastIdx) pushText(repaired.slice(lastIdx, m.index));

    const label = resolve(m[0]).trim();
    if (label) {
      ensureSpaceBeforeMark();
      segments.push({ text: label, ingredient: true });
    }

    lastIdx = m.index! + m[0].length;
  }

  if (lastIdx < repaired.length) pushText(repaired.slice(lastIdx));

  if (segments.length === 0) {
    return [
      {
        text: resolveFullInstruction(
          text,
          ingredients,
          selectedServings,
          baseServings,
          locale,
          unitSystem,
        ),
        ingredient: false,
      },
    ];
  }

  return segments;
}
